import {
    People as AccountsIcon,
    FastfoodOutlined as Category,
    Apartment as CompaniesIcon,
    PeopleOutline as CompanyOwnersIcon,
    Login as LoginIcon,
    Logout as LogoutIcon,
    Receipt as Order,
    Place as PlaceIcon,
    Fastfood as Product,
    TabletAndroid as TerminalsIcon,
    Web as WebsiteIcon,
    EmojiFoodBeverage as NestedProductIcon,
    EmojiFoodBeverageOutlined as NestedProductsCategoryIcon,
    Discount as PromotionIcon,
} from '@mui/icons-material';
import { useWorkspace } from 'hooks/useWorkspace';
import type { LinkItem } from '../types';
import { useTranslation } from 'react-i18next';

/**
 * App Navigation Link Items
 */

export const useLinkItemsAdmin: () => LinkItem[] = () => {
    const { t } = useTranslation();
    const { baseWorkspaceUrl, placeId } = useWorkspace();
    const isPlaceValid = placeId === '' || placeId === 'undefined';

    return [
        {
            id: 1,
            name: t('LINK_ITEMS.COMPANY_OWNERS'),
            description: 'Accounts Page',
            to: `${baseWorkspaceUrl}/company-owners`,
            icon: <AccountsIcon />,
            disabled: false,
        },
        {
            id: 2,
            name: t('LINK_ITEMS.END_USERS'),
            description: 'End Users Page',
            to: `${baseWorkspaceUrl}/end-users`,
            icon: <CompanyOwnersIcon />,
            disabled: false,
        },
        {
            id: 3,
            name: t('LINK_ITEMS.COMPANIES'),
            description: 'Companies Page',
            to: `${baseWorkspaceUrl}/companies`,
            icon: <CompaniesIcon />,
            disabled: false,
        },
        {
            id: 4,
            name: t('LINK_ITEMS.WEBSITES'),
            description: 'Websites Page',
            to: `${baseWorkspaceUrl}/websites`,
            icon: <WebsiteIcon />,
            disabled: false,
        },
        {
            id: 5,
            name: t('LINK_ITEMS.TERMINALS'),
            description: 'Terminals Page',
            to: `${baseWorkspaceUrl}/terminals`,
            icon: <TerminalsIcon />,
            disabled: false,
        },
        {
            id: 6,
            name: t('LINK_ITEMS.PLACES'),
            description: 'Places Page',
            to: `${baseWorkspaceUrl}/places`,
            icon: <PlaceIcon />,
            disabled: false,
        },
        {
            id: 7,
            name: t('LINK_ITEMS.PRODUCTS'),
            description: 'Products Page',
            to: `${baseWorkspaceUrl}/products/${placeId}`,
            icon: <Product />,
            disabled: isPlaceValid,
        },
        {
            id: 8,
            name: t('LINK_ITEMS.PRODUCT_CATEGORIES'),
            description: 'Product Categories Page',
            to: `${baseWorkspaceUrl}/productCategories`,
            icon: <Category />,
            disabled: false,
        },
        {
            id: 9,
            name: t('LINK_ITEMS.NESTED_PRODUCTS'),
            description: 'Nested Products Page',
            to: `${baseWorkspaceUrl}/nestedProducts/${placeId}`,
            icon: <NestedProductIcon />,
            disabled: isPlaceValid,
        },
        {
            id: 10,
            name: t('LINK_ITEMS.NESTED_PRODUCT_CATEGORIES'),
            description: 'Nested Products Categories Page',
            to: `${baseWorkspaceUrl}/nestedProductCategories`,
            icon: <NestedProductsCategoryIcon />,
            disabled: false,
        },
        {
            id: 11,
            name: t('LINK_ITEMS.PROMOTIONS'),
            description: 'Promotions Page',
            to: `${baseWorkspaceUrl}/promotions/${placeId}`,
            icon: <PromotionIcon />,
            disabled: isPlaceValid,
        },
        {
            id: 12,
            name: t('LINK_ITEMS.ORDERS'),
            description: 'Orders Page',
            to: `${baseWorkspaceUrl}/orders/${placeId}`,
            icon: <Order />,
            disabled: isPlaceValid,
        },
    ];
};

export const useLinkItemsCompanyOwner: () => LinkItem[] = () => {
    const { t } = useTranslation();
    const { baseWorkspaceUrl, placeId } = useWorkspace();
    const isPlaceValid = placeId === '';

    return [
        {
            id: 1,
            name: t('LINK_ITEMS.PLACES'),
            description: 'Place Page',
            to: `${baseWorkspaceUrl}/places`,
            icon: <CompaniesIcon />,
            disabled: false,
        },
        {
            id: 2,
            name: t('LINK_ITEMS.PRODUCTS'),
            description: 'Products Page',
            to: `${baseWorkspaceUrl}/products/${placeId}`,
            icon: <Product />,
            disabled: isPlaceValid,
        },
        {
            id: 3,
            name: t('LINK_ITEMS.PRODUCT_CATEGORIES'),
            description: 'Product Categories Page',
            to: `${baseWorkspaceUrl}/productCategories`,
            icon: <Category />,
            disabled: false,
        },
        {
            id: 4,
            name: t('LINK_ITEMS.NESTED_PRODUCTS'),
            description: 'Nested Products Page',
            to: `${baseWorkspaceUrl}/nestedProducts/${placeId}`,
            icon: <NestedProductIcon />,
            disabled: isPlaceValid,
        },
        {
            id: 5,
            name: t('LINK_ITEMS.NESTED_PRODUCT_CATEGORIES'),
            description: 'Nested Products Categories Page',
            to: `${baseWorkspaceUrl}/nestedProductCategories`,
            icon: <NestedProductsCategoryIcon />,
            disabled: false,
        },
        {
            id: 6,
            name: t('LINK_ITEMS.PROMOTIONS'),
            description: 'Promotions Page',
            to: `${baseWorkspaceUrl}/promotions/${placeId}`,
            icon: <PromotionIcon />,
            disabled: isPlaceValid,
        },
        {
            id: 7,
            name: t('LINK_ITEMS.ORDERS'),
            description: 'Orders Page',
            to: `${baseWorkspaceUrl}/orders/${placeId}`,
            icon: <Order />,
            disabled: isPlaceValid,
        },
    ];
};

/**
 * App Authentication Link Items
 */
export const authItems: LinkItem[] = [
    {
        id: 1,
        name: 'login',
        description: 'Login',
        to: '/login',
        icon: <LoginIcon />,
    },
    {
        id: 2,
        name: 'logout',
        description: 'Logout',
        to: '/login',
        icon: <LogoutIcon />,
    },
];
